import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { isNonNullish } from 'remeda';
import { startOfDay } from '@/utils';
import {
  type MatchesInitialState,
  type SetDailyMatchesPayload,
  type EditMatchPayload,
  type EditCompetitionPayload,
  type Match,
  type addHomeWidgetPayload,
} from './types';

const initialState: MatchesInitialState = {
  dailyMatches: {},
  selectedDate: startOfDay(),
  selectedSport: 'all',
  selectedCompetition: '',
  editedMatches: [],
  editedCompetitions: [],
  addLiveBlog: [],
  addHomeWidget: [],
  syncMatchData: [],
};

export const matchesSlice = createSlice({
  name: 'matches',
  initialState,
  reducers: {
    setDailyMatches: (
      state,
      {
        payload: { matches, selectedDate },
      }: PayloadAction<SetDailyMatchesPayload>,
    ) => {
      if (selectedDate) {
        state.dailyMatches[selectedDate] = matches;
      }
    },
    clearDailyMatches: (state) => {
      state.dailyMatches = {};
    },
    clearAll: (state) => {
      state.dailyMatches = {};
      state.editedMatches = [];
      state.editedCompetitions = [];
      state.addLiveBlog = [];
      state.addHomeWidget = [];
      state.syncMatchData = [];
    },
    setSelectedDate: (state, { payload }: PayloadAction<string | null>) => {
      state.selectedDate = payload;
    },
    setSelectedSport: (state, { payload }: PayloadAction<string>) => {
      state.selectedSport = payload;
    },
    setSelectedCompetition: (state, { payload }: PayloadAction<string>) => {
      state.selectedCompetition = payload;
    },
    localUpdateMatch: (
      state,
      {
        payload: {
          id,
          home_competitor_name,
          away_competitor_name,
          start_time,
          home_score,
          away_score,
          home_competitor_logo,
          away_competitor_logo,
          status,
        },
      }: PayloadAction<EditMatchPayload>,
    ) => {
      const { selectedDate } = state;

      if (selectedDate) {
        const matches = state.dailyMatches[selectedDate];
        const match = matches?.find((match) => match.id === id);

        if (match) {
          if (home_competitor_name) {
            match.home_team.name = home_competitor_name;
            match.edit.home_competitor_name = home_competitor_name;
          }

          if (away_competitor_name) {
            match.away_team.name = away_competitor_name;
            match.edit.away_competitor_name = away_competitor_name;
          }

          if (start_time) {
            match.start_time = start_time;
            match.edit.start_time = start_time;
          }

          if (home_competitor_logo) {
            match.home_team.logo_url = home_competitor_logo;
          }

          if (away_competitor_logo) {
            match.away_team.logo_url = away_competitor_logo;
          }

          if (status) {
            match.status = status;
          }

          const alreeadyEditedIndex = state.editedMatches.findIndex(
            (item) => item.id === id,
          );
          if (alreeadyEditedIndex === -1) {
            state.editedMatches = [
              ...state.editedMatches,
              {
                id,
                home_competitor_name,
                away_competitor_name,
                start_time,
                home_score,
                away_score,
                home_competitor_logo,
                away_competitor_logo,
                status,
              },
            ];
          } else {
            state.editedMatches[alreeadyEditedIndex] = {
              ...state.editedMatches[alreeadyEditedIndex],
              home_competitor_name,
              away_competitor_name,
              start_time,
              home_score,
              away_score,
              home_competitor_logo,
              away_competitor_logo,
              status,
            };
          }
        }
      }
    },
    localUpdateCompetition: (
      state,
      {
        payload: { external_id, name, category_name, ordinal, syncData },
      }: PayloadAction<EditCompetitionPayload>,
    ) => {
      const { selectedDate } = state;

      if (selectedDate) {
        const matches = state.dailyMatches[selectedDate];

        matches?.forEach((match) => {
          if (match.competition.external_id === external_id) {
            if (name) {
              match.competition.name = name;
              match.competition.edit.name = name;
            }

            if (category_name) {
              match.category.name = category_name;
              match.category.edit.name = category_name;
            }

            if (isNonNullish(ordinal)) match.competition.ordinal = ordinal;

            const alreeadyEditedIndex = state.editedCompetitions.findIndex(
              (item) => item.external_id === external_id,
            );
            if (alreeadyEditedIndex === -1) {
              state.editedCompetitions = [
                ...state.editedCompetitions,
                {
                  external_id,
                  name,
                  category_name,
                  syncData,
                  ...(isNonNullish(ordinal) ? { ordinal } : {}),
                  start_time: selectedDate,
                },
              ];
            } else {
              state.editedCompetitions[alreeadyEditedIndex] = {
                ...state.editedCompetitions[alreeadyEditedIndex],
                name,
                category_name,
                syncData,
                ...(isNonNullish(ordinal) ? { ordinal } : {}),
                start_time: selectedDate,
              };
            }
          }
        });
      }
    },
    addLiveBlog: (state, { payload }: PayloadAction<Match['id']>) => {
      state.addLiveBlog = [
        ...(state.addLiveBlog.includes(payload)
          ? state.addLiveBlog.filter((item) => item !== payload)
          : [...state.addLiveBlog, payload]),
      ];
    },
    addHomeWidget: (
      state,
      { payload }: PayloadAction<addHomeWidgetPayload>,
    ) => {
      const exists = state.addHomeWidget.some(({ id }) => id === payload.id);
      if (exists)
        state.addHomeWidget = state.addHomeWidget.filter(
          ({ id }) => id !== payload.id,
        );
      state.addHomeWidget = [...state.addHomeWidget, payload];
    },
    syncMatchData: (state, { payload }: PayloadAction<Match['id']>) => {
      state.syncMatchData = [
        ...(state.syncMatchData.includes(payload)
          ? state.syncMatchData.filter((item) => item !== payload)
          : [...state.syncMatchData, payload]),
      ];
    },
  },
});

export const { reducer: matchesReducer } = matchesSlice;
